import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Access token lives in memory only (refresh token is an httpOnly cookie)
let accessToken = null;
export const setAccessToken = (token) => { accessToken = token; };
export const getAccessToken = () => accessToken;

// Create axios instance with auth header
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Handle 401 responses: silently refresh the access token once, then retry
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const url = original.url || '';
    const isAuthCall = url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/register') || url.includes('/auth/logout');
    if (error.response?.status === 401 && !original._retry && !isAuthCall) {
      original._retry = true;
      try {
        const r = await refreshSession();
        setAccessToken(r.data.token);
        original.headers = { ...original.headers, Authorization: `Bearer ${r.data.token}` };
        return api(original);
      } catch (refreshErr) {
        setAccessToken(null);
        localStorage.removeItem('doorguard_token');
        localStorage.removeItem('doorguard_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const register = (data) => api.post('/auth/register', data);
export const login = (data) => api.post('/auth/login', data);
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const resetPassword = (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword });
// Dedupe concurrent refresh calls (StrictMode double-mount, multi-component 401s)
let refreshPromise = null;
export const refreshSession = () => {
  if (!refreshPromise) {
    refreshPromise = api.post('/auth/refresh').finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};
export const serverLogout = () => api.post('/auth/logout');
export const changePassword = (currentPassword, newPassword) => api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword });
export const updateNotificationPrefs = (prefs) => api.put('/account/notifications', prefs);
export const deleteAccount = (password) => api.post('/account/delete', { password });
export const getTrainerEarnings = () => api.get('/trainers/earnings');
export const getMe = () => api.get('/auth/me');

// Profile
export const updateClientProfile = (data) => api.put('/profile/client', data);
export const updateClientFullProfile = (data) => api.put('/profile/client-full', data);
export const updateTrainerProfile = (data) => api.put('/profile/trainer', data);
export const updateTrainerFullProfile = (data) => api.put('/profile/trainer-full', data);
export const selectCategory = (category) => api.put('/profile/category', null, { params: { category } });
export const acceptLiabilityWaiver = () => api.post('/profile/accept-waiver');
export const acceptTrainerAgreement = () => api.post('/profile/accept-trainer-agreement');

// Trainers
export const getTrainers = (params) => api.get('/trainers', { params });
export const getTrainer = (id) => api.get(`/trainers/${id}`);

// Sessions
export const bookSession = (data) => api.post('/sessions/book', data);
export const getClientSessions = () => api.get('/sessions/client');
export const getTrainerSessions = () => api.get('/sessions/trainer');
export const getSession = (id) => api.get(`/sessions/${id}`);
export const updateSessionStatus = (sessionId, status) => api.put(`/sessions/${sessionId}/status`, null, { params: { status } });
export const acceptSession = (sessionId) => api.post(`/sessions/${sessionId}/accept`);
export const declineSession = (sessionId) => api.post(`/sessions/${sessionId}/decline`);
export const cancelSession = (sessionId, reason) => api.post(`/sessions/${sessionId}/cancel`, { reason });

// Availability
export const setTrainerOnline = (isOnline) => api.post('/availability/online', { is_online_now: !!isOnline });
export const getTrainerAvailability = (trainerId, date) => api.get(`/trainers/${trainerId}/availability`, { params: { date } });

// Challenges
export const getChallenges = () => api.get('/challenges');
export const completeChallenge = (challengeId) => api.post(`/challenges/${challengeId}/complete`);
export const phaseUp = () => api.post('/progress/phase-up');

// Reviews
export const createReview = (data) => api.post('/reviews', data);
export const getTrainerReviews = (trainerId) => api.get(`/reviews/trainer/${trainerId}`);
export const getMyReviewForTrainer = (trainerId) => api.get(`/reviews/trainer/${trainerId}/mine`);

// Payments (legacy Checkout, used by old session flow)
export const createCheckout = (sessionId) => api.post('/payments/create-checkout', null, { params: { session_id: sessionId } });
export const getPaymentStatus = (stripeSessionId) => api.get(`/payments/status/${stripeSessionId}`);

// Stripe Connect (trainers receive payouts) + Payment Element (clients pay)
export const getStripeConfig = () => api.get('/config/stripe');
export const stripeConnectOnboard = () => api.post('/stripe/connect/onboard');
export const stripeConnectStatus = () => api.get('/stripe/connect/status');
export const createPaymentIntent = (data) => api.post('/bookings/create-payment-intent', data);
export const confirmBooking = (paymentIntentId) => api.post('/bookings/confirm', { payment_intent_id: paymentIntentId });

// Stats
export const getTrainerStats = () => api.get('/stats/trainer');
export const getClientStats = () => api.get('/stats/client');

// Safety & Trust
export const getEmergencyContacts = () => api.get('/safety/emergency-contacts');
export const updateEmergencyContacts = (contacts) => api.put('/safety/emergency-contacts', { contacts });
export const triggerSOS = (data) => api.post('/safety/sos', data);
export const getSafetyAlerts = () => api.get('/safety/alerts');
export const resolveAlert = (alertId) => api.put(`/safety/alerts/${alertId}/resolve`);
export const createIncidentReport = (data) => api.post('/safety/report', data);
export const getIncidentReports = () => api.get('/safety/reports');
export const sessionCheckIn = (sessionId) => api.post(`/sessions/${sessionId}/checkin`);
export const sessionCheckOut = (sessionId) => api.post(`/sessions/${sessionId}/checkout`);

// Admin (Trust & Safety dashboard), gated by ADMIN_EMAILS env var on the backend
export const adminSafetySummary = () => api.get('/admin/safety/summary');
export const adminListAlerts = (status) => api.get('/admin/safety/alerts', { params: status ? { status } : {} });
export const adminResolveAlert = (alertId) => api.put(`/admin/safety/alerts/${alertId}/resolve`);
export const adminListReports = (status) => api.get('/admin/safety/reports', { params: status ? { status } : {} });
export const adminUpdateReportStatus = (reportId, status) => api.put(`/admin/safety/reports/${reportId}/status`, null, { params: { status } });
// Waitlist removed (Jun 2026)

// Trainer Packages
export const createPackage = (data) => api.post('/packages', data);
export const getMyPackages = () => api.get('/packages/mine');
export const getTrainerPackages = (trainerId) => api.get(`/packages/trainer/${trainerId}`);
export const updatePackage = (id, data) => api.put(`/packages/${id}`, data);
export const deletePackage = (id) => api.delete(`/packages/${id}`);
export const createPackagePaymentIntent = (packageId) => api.post('/packages/purchase/create-payment-intent', { package_id: packageId });
export const confirmPackagePurchase = (paymentIntentId) => api.post('/packages/purchase/confirm', { payment_intent_id: paymentIntentId });
export const getMyPackagePurchases = () => api.get('/packages/purchases/mine');
export const bookSessionWithPackage = (data) => api.post('/packages/book-session', data);

// Trainer Applications
export const submitTrainerApplication = (data) => api.post('/applications/trainer', data);
export const adminListApplications = (status) => api.get('/admin/applications', { params: status ? { status } : {} });
export const adminApproveApplication = (id, notes) => api.post(`/admin/applications/${id}/approve`, { notes });
export const adminRejectApplication = (id, notes) => api.post(`/admin/applications/${id}/reject`, { notes });

// Client Onboarding
export const submitOnboardingQuiz = (data) => api.post('/profile/onboarding-quiz', data);

// Messages
export const sendMessage = (recipient_id, body) => api.post('/messages', { recipient_id, body });
export const listMessageThreads = () => api.get('/messages/threads');
export const getMessageThread = (peerId) => api.get(`/messages/with/${peerId}`);

// Admin Dashboard
export const adminDashboardStats = () => api.get('/admin/dashboard/stats');
export const adminListCoaches = () => api.get('/admin/coaches');
export const adminGetCoach = (id) => api.get(`/admin/coaches/${id}`);
export const adminSetCoachStatus = (id, status) => api.put(`/admin/coaches/${id}/status`, { status });
export const adminDeleteCoach = (id) => api.delete(`/admin/coaches/${id}`);
export const adminEditCoach = (id, payload) => api.put(`/admin/coaches/${id}`, payload);
export const adminSetCoachVisibility = (id, hidden) => api.put(`/admin/coaches/${id}/visibility`, { value: !!hidden });
export const adminSetCoachFeature = (id, featured) => api.put(`/admin/coaches/${id}/feature`, { value: !!featured });
export const adminSetCoachVerification = (id, level) => api.put(`/admin/coaches/${id}/verify`, { verification_level: level });
export const adminBanCoach = (id, banned, reason) => api.put(`/admin/coaches/${id}/ban`, { banned: !!banned, reason });
export const adminListClients = () => api.get('/admin/clients');
export const adminSetClientStatus = (id, isActive) => api.put(`/admin/clients/${id}/status`, { is_active: !!isActive });
export const adminDeleteClient = (id) => api.delete(`/admin/clients/${id}`);
export const adminBanClient = (id, banned, reason) => api.put(`/admin/clients/${id}/ban`, { banned: !!banned, reason });

// Coach availability blocks (multi-block per weekday)
export const addAvailabilityBlock = (day_key, start, end) =>
  api.post('/trainers/availability', { day_key, start, end });
export const deleteAvailabilityBlock = (day_key, index) =>
  api.delete(`/trainers/availability/${day_key}/${index}`);

// Live trainer location
export const updateTrainerLiveLocation = (lat, lng) => api.post('/availability/location', { lat, lng });

// Favorites (iter-11)
export const addFavoriteTrainer = (trainerId) => api.post(`/favorites/${trainerId}`);
export const removeFavoriteTrainer = (trainerId) => api.delete(`/favorites/${trainerId}`);
export const listFavoriteTrainers = () => api.get('/favorites');

// DG Points
export const getDgPointsBalance = () => api.get('/dgpoints/balance');
export const getDgPointsHistory = () => api.get('/dgpoints/history');
export const submitSocialClaim = (action, screenshot) => api.post('/dgpoints/claims', { action, screenshot });
export const adminListClaims = () => api.get('/admin/claims');
export const adminApproveClaim = (id) => api.post(`/admin/claims/${id}/approve`);
export const adminDenyClaim = (id) => api.post(`/admin/claims/${id}/deny`);

// Referrals
export const getMyReferral = () => api.get('/referrals/me');

// Coach notes
export const addCoachNote = (sessionId, note) => api.post(`/sessions/${sessionId}/note`, { note });

// Belt XP + challenges
export const getCoachXpSettings = () => api.get('/coach/xp-settings');
export const updateCoachXpSettings = (data) => api.put('/coach/xp-settings', data);
export const listCoachChallenges = () => api.get('/coach/challenges');
export const createCoachChallenge = (description, xp_reward) => api.post('/coach/challenges', { description, xp_reward });
export const deleteCoachChallenge = (id) => api.delete(`/coach/challenges/${id}`);
export const awardClientXp = (data) => api.post('/coach/award-xp', data);
export const getBeltStatus = (clientId) => api.get(`/clients/${clientId}/belt-status`);
export const requestPromotion = (trainer_id, discipline) => api.post('/coach/request-promotion', { trainer_id, discipline });
export const coachPromote = (data) => api.post('/coach/promote', data);
export const coachPendingPromotions = () => api.get('/coach/pending-promotions');
export const coachCompleteSession = (sessionId, discipline) => api.post(`/sessions/${sessionId}/complete`, { discipline });

// Bans
export const adminListBans = () => api.get('/admin/bans');
export const adminCreateBan = (data) => api.post('/admin/bans', data);
export const adminRemoveBan = (banId) => api.delete(`/admin/bans/${banId}`);

// Phone auth removed (Jun 2026) — email + password only

// Curriculum (iter-12)
export const getCurriculumDefaults = () => api.get('/curriculum/defaults');
export const getMyCurriculum = () => api.get('/coach/curriculum');
export const updateMyCurriculum = (discipline, phases) => api.put('/coach/curriculum', { discipline, phases });
export const resetMyCurriculum = (discipline) => api.post(`/coach/curriculum/reset/${discipline}`);
export const getTrainerCurriculum = (trainerId) => api.get(`/trainers/${trainerId}/curriculum`);

// Group sessions / seminars (iter-12)
export const createGroupEvent = (data) => api.post('/group-events', data);
export const listGroupEvents = (trainerId) => api.get('/group-events', { params: trainerId ? { trainer_id: trainerId } : {} });
export const getGroupEvent = (id) => api.get(`/group-events/${id}`);
export const bookGroupEvent = (id, liability_accepted) => api.post(`/group-events/${id}/book`, { liability_accepted });
export const completeGroupAttendee = (eventId, clientId) => api.post(`/group-events/${eventId}/complete/${clientId}`);
export const deleteGroupEvent = (id) => api.delete(`/group-events/${id}`);

export default api;
